import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface LocationType {
    id: string;
    name: string;
    pos: string;
    status: 'Active' | 'Inactive';
}

export interface LocationTypeWritePayload {
    name?: string;
    pos?: string;
    status?: 'Active' | 'Inactive';
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

function normalize(row: { id: string; name: string; pos: string; status: string }): LocationType {
    return { id: row.id, name: row.name, pos: row.pos || '', status: normalizeStatus(row.status) };
}

export function useLocationTypes() {
    return useQuery({
        queryKey: ['location-types'] as const,
        queryFn: async () => {
            const res = await api.get('/location-types');
            return (res.data as Array<{ id: string; name: string; pos: string; status: string }>).map(normalize);
        },
        staleTime: 60_000,
    });
}

export function useCreateLocationType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: LocationTypeWritePayload) => {
            const res = await api.post('/location-types', payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['location-types'] }),
    });
}

export function useUpdateLocationType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: LocationTypeWritePayload }) => {
            const res = await api.patch(`/location-types/${id}`, payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['location-types'] }),
    });
}

export function useDeleteLocationType() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/location-types/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['location-types'] }),
    });
}
